import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, CheckCircle, Circle, AlertTriangle, ThumbsUp, Loader2 } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface HealthCheckPanelProps {
    client: CJMSClient;
}

const HEALTH_CHECKS = [
    { id: 'usage', label: 'Platform Usage', desc: 'Is the client actively using the platform?' },
    { id: 'satisfaction', label: 'Satisfaction Score', desc: 'Has the client expressed positive feedback?' },
    { id: 'results', label: 'Early Results', desc: 'Are there measurable outcomes from the service?' },
    { id: 'engagement', label: 'Engagement Trend', desc: 'Is engagement stable or growing?' },
    { id: 'support', label: 'Support Needs', desc: 'Are there unresolved support tickets?' },
    { id: 'referral', label: 'Referral Readiness', desc: 'Would the client refer others?' },
    { id: 'renewal', label: 'Renewal Risk', desc: 'Is the client at risk of churning?' },
];

export const HealthCheckPanel: React.FC<HealthCheckPanelProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Retention' || client.currentStage === 'Growth';
    const [checks, setChecks] = useState<Record<string, boolean>>({});
    const [status, setStatus] = useState<'idle' | 'saving'>('idle');

    if (!isEligible) return null;

    const toggleCheck = (id: string) => {
        setChecks(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const completedChecks = Object.values(checks).filter(Boolean).length;
    const healthScore = Math.round((completedChecks / HEALTH_CHECKS.length) * 100);

    const getHealthColor = (score: number) => {
        if (score >= 80) return 'text-green-600';
        if (score >= 50) return 'text-amber-600';
        return 'text-red-600';
    };

    const getHealthBg = (score: number) => {
        if (score >= 80) return 'bg-green-50 border-green-200';
        if (score >= 50) return 'bg-amber-50 border-amber-200';
        return 'bg-red-50 border-red-200';
    };

    const handleSave = async () => {
        setStatus('saving');
        await new Promise(r => setTimeout(r, 500));
        feedbackBus.emit('activity_completed', {
            activityLabel: `Health check completed: score ${healthScore}%`,
            clientEmail: client.email,
        });
        setStatus('idle');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-rose-600" />
                    <h3 className="text-sm font-bold text-slate-800">7-Day Health Check</h3>
                </div>
                <div className={`px-2 py-1 rounded-full text-[10px] font-bold border ${getHealthBg(healthScore)} ${getHealthColor(healthScore)}`}>
                    {healthScore}% Healthy
                </div>
            </div>
            <div className="p-4 space-y-2">
                <div className="flex items-center gap-2 p-2 bg-rose-50 rounded-lg border border-rose-100 mb-3">
                    <ThumbsUp className="w-3 h-3 text-rose-600" />
                    <p className="text-[10px] text-rose-700">Mark completed items to calculate health score</p>
                </div>
                {HEALTH_CHECKS.map(check => (
                    <div
                        key={check.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                            checks[check.id]
                                ? 'bg-green-50 border-green-100'
                                : 'bg-white border-slate-100 hover:border-rose-200'
                        }`}
                        onClick={() => toggleCheck(check.id)}
                    >
                        {checks[check.id]
                            ? <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                            : <Circle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />}
                        <div className="flex-1 min-w-0">
                            <span className={`text-xs font-semibold ${checks[check.id] ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                                {check.label}
                            </span>
                            <p className="text-[10px] text-slate-500 mt-0.5">{check.desc}</p>
                        </div>
                    </div>
                ))}
                <div className="flex justify-end pt-2 border-t border-slate-100 mt-4">
                    <button
                        onClick={handleSave}
                        disabled={status === 'saving'}
                        className="px-3 py-1.5 text-[11px] font-medium bg-rose-600 text-white rounded-md hover:bg-rose-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                        {status === 'saving' && <Loader2 className="w-3 h-3 animate-spin" />}
                        Complete Health Check
                    </button>
                </div>
            </div>
        </motion.div>
    );
};
